import { useState } from 'react';
import { RgbaColor } from 'react-colorful';

import './rgba-inputs.css';

type RgbaKey = keyof RgbaColor;

type RGBAInputsProps = {
  /** prefix for the fields' ids, so each picker on a page gets its own */
  idPrefix: string;
  color: RgbaColor;
  onChange: (color: RgbaColor) => void;
};

const RGBA_FIELDS: { rgbaKey: RgbaKey; label: string; ariaLabel: string }[] = [
  { rgbaKey: 'r', label: 'R', ariaLabel: 'Red' },
  { rgbaKey: 'g', label: 'G', ariaLabel: 'Green' },
  { rgbaKey: 'b', label: 'B', ariaLabel: 'Blue' },
  { rgbaKey: 'a', label: 'A', ariaLabel: 'Alpha' },
];

const isInRgbaRange = (rgbaKey: RgbaKey, value: string) => {
  // 0.00-1.00, allowing 0 and 1 on their own
  if (rgbaKey === 'a') {
    return /^(1(\.0{1,2})?|0(\.\d{1,2})?)$/.test(value);
  }

  // 0-255
  return /^\d{1,3}$/.test(value) && Number(value) <= 255;
};

/**
 * The R, G, B and A fields of the color picker. Like the hex field, a field being edited keeps what the
 * user typed - a valid value applies as it's typed, and an emptied field becomes 0 when it's left.
 */
const RGBAInputs = ({ idPrefix, color, onChange }: RGBAInputsProps) => {
  // what the user has typed into each field since they started editing it
  const [drafts, setDrafts] = useState<Partial<Record<RgbaKey, string>>>({});

  const setDraft = (rgbaKey: RgbaKey, value: string | undefined) =>
    setDrafts((current) => {
      const next = { ...current, [rgbaKey]: value };
      if (value === undefined) delete next[rgbaKey];
      return next;
    });

  return (
    <div className="flex fd-row jc-center ai-center rgba-inputs-container">
      {RGBA_FIELDS.map(({ rgbaKey, label, ariaLabel }) => {
        const id = `${idPrefix}-${label}`;
        return (
          <div key={rgbaKey} className="flex-column jc-center ai-center">
            <input
              id={id}
              aria-label={ariaLabel}
              className="radius-100 clear-input text-center"
              type="number"
              min={0}
              max={rgbaKey === 'a' ? 1 : 255}
              step={rgbaKey === 'a' ? 0.01 : 1}
              style={rgbaKey === 'a' ? { width: '48.5px' } : undefined}
              value={drafts[rgbaKey] ?? String(color[rgbaKey])}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  setDraft(rgbaKey, '');
                  return;
                }
                if (!isInRgbaRange(rgbaKey, value)) return;
                setDraft(rgbaKey, value);
                onChange({ ...color, [rgbaKey]: Number(value) });
              }}
              onBlur={() => {
                if (drafts[rgbaKey] === '') onChange({ ...color, [rgbaKey]: 0 });
                setDraft(rgbaKey, undefined);
              }}
            />
            <label className="width-100-percent flex fd-row jc-center ai-center" htmlFor={id}>
              {label}
            </label>
          </div>
        );
      })}
    </div>
  );
};

export default RGBAInputs;
